import { jsPDF } from 'jspdf';

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 16;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const COL_GAP = 8;
const LEFT_COL = (CONTENT_WIDTH - COL_GAP) / 2;
const RIGHT_COL_X = MARGIN + LEFT_COL + COL_GAP;

const COLORS = {
  slate900: [15, 23, 42],
  slate700: [51, 65, 85],
  slate500: [100, 116, 139],
  slate400: [148, 163, 184],
  slate200: [226, 232, 240],
  slate100: [241, 245, 249],
  violet600: [124, 58, 237],
  white: [255, 255, 255],
};

const loadLogoDataUrl = async () => {
  const url = `${process.env.PUBLIC_URL || ''}/sagemcom-logo.png`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Logo not found');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const formatPdfTimestamp = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-KE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso || '');
  }
};

const formatPdfDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-KE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(iso || '—');
  }
};

const setColor = (doc, rgb) => doc.setTextColor(...rgb);
const setDraw = (doc, rgb) => doc.setDrawColor(...rgb);
const setFill = (doc, rgb) => doc.setFillColor(...rgb);

const ensureSpace = (doc, y, needed = 12) => {
  if (y + needed <= PAGE_HEIGHT - 22) return y;
  doc.addPage();
  return MARGIN;
};

const drawPageFooter = (doc) => {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    setDraw(doc, COLORS.slate200);
    doc.line(MARGIN, PAGE_HEIGHT - 14, MARGIN + CONTENT_WIDTH, PAGE_HEIGHT - 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setColor(doc, COLORS.slate400);
    doc.text('Sagemcom · Customer Services Kenya', MARGIN, PAGE_HEIGHT - 8);
    doc.text(`Page ${i} of ${pages}`, MARGIN + CONTENT_WIDTH, PAGE_HEIGHT - 8, { align: 'right' });
  }
};

const drawHeader = (doc, logoData, referenceId) => {
  setFill(doc, COLORS.slate100);
  doc.rect(0, 0, PAGE_WIDTH, 36, 'F');

  doc.addImage(logoData, 'PNG', MARGIN, 10, 44, 15);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  setColor(doc, COLORS.slate900);
  doc.text('Customer Services Kenya', MARGIN + 50, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setColor(doc, COLORS.slate500);
  doc.text('Sagemcom Logistics', MARGIN + 50, 22);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, COLORS.violet600);
  doc.text(String(referenceId || ''), MARGIN + CONTENT_WIDTH, 16, { align: 'right' });

  setDraw(doc, COLORS.violet600);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, 38, MARGIN + CONTENT_WIDTH, 38);
  doc.setLineWidth(0.2);

  return 46;
};

const drawReportTitle = (doc, y, reportTitle, generatedAt) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  setColor(doc, COLORS.slate900);
  doc.text(reportTitle, MARGIN, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setColor(doc, COLORS.slate500);
  doc.text(`Generated ${generatedAt}`, MARGIN, y + 6);

  return y + 14;
};

const drawSummaryStrip = (doc, y, items) => {
  const rowHeight = 14;
  const boxHeight = Math.ceil(items.length / 3) * rowHeight + 8;
  y = ensureSpace(doc, y, boxHeight + 4);

  setFill(doc, COLORS.slate100);
  setDraw(doc, COLORS.slate200);
  doc.roundedRect(MARGIN, y, CONTENT_WIDTH, boxHeight, 2, 2, 'FD');

  const colWidth = CONTENT_WIDTH / 3;
  items.forEach((item, index) => {
    const col = index % 3;
    const row = Math.floor(index / 3);
    const x = MARGIN + 6 + col * colWidth;
    const itemY = y + 8 + row * rowHeight;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    setColor(doc, COLORS.slate500);
    doc.text(String(item.label).toUpperCase(), x, itemY);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    setColor(doc, COLORS.slate900);
    const valueLines = doc.splitTextToSize(String(item.value || '—'), colWidth - 8);
    doc.text(valueLines[0] || '—', x, itemY + 4.5);
  });

  return y + boxHeight + 8;
};

const drawSectionHeading = (doc, y, title) => {
  y = ensureSpace(doc, y, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setColor(doc, COLORS.slate700);
  doc.text(title.toUpperCase(), MARGIN, y);
  setDraw(doc, COLORS.slate200);
  doc.line(MARGIN, y + 2, MARGIN + CONTENT_WIDTH, y + 2);
  return y + 8;
};

const drawTextBox = (doc, y, text, minHeight = 18) => {
  y = ensureSpace(doc, y, minHeight + 6);
  const lines = doc.splitTextToSize(String(text || '—'), CONTENT_WIDTH - 10);
  const boxHeight = Math.max(minHeight, lines.length * 4.8 + 8);

  setFill(doc, COLORS.white);
  setDraw(doc, COLORS.slate200);
  doc.roundedRect(MARGIN, y, CONTENT_WIDTH, boxHeight, 2, 2, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setColor(doc, COLORS.slate900);
  doc.text(lines, MARGIN + 5, y + 7);

  return y + boxHeight + 8;
};

const drawFieldColumn = (doc, startX, colWidth, startY, fields) => {
  let y = startY;
  fields.forEach(({ label, value }) => {
    if (value == null || value === '') return;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setColor(doc, COLORS.slate500);
    doc.text(label, startX, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    setColor(doc, COLORS.slate900);
    const lines = doc.splitTextToSize(String(value), colWidth);
    doc.text(lines, startX, y + 4);
    y += 4 + lines.length * 4.5 + 3;
  });
  return y;
};

const drawTwoColumnSection = (doc, y, leftTitle, leftFields, rightTitle, rightFields) => {
  y = ensureSpace(doc, y, 24);
  const sectionTop = y;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, COLORS.slate700);
  doc.text(leftTitle, MARGIN, y);
  doc.text(rightTitle, RIGHT_COL_X, y);
  y += 5;

  const leftEnd = drawFieldColumn(doc, MARGIN, LEFT_COL, y, leftFields);
  const rightEnd = drawFieldColumn(doc, RIGHT_COL_X, LEFT_COL, y, rightFields);
  const sectionEnd = Math.max(leftEnd, rightEnd, sectionTop + 16);

  setDraw(doc, COLORS.slate200);
  doc.line(MARGIN + LEFT_COL + COL_GAP / 2, sectionTop - 2, MARGIN + LEFT_COL + COL_GAP / 2, sectionEnd);

  return sectionEnd + 6;
};

const drawTimeline = (doc, y, updates) => {
  if (!updates.length) return y;

  y = drawSectionHeading(doc, y, 'Activity timeline');

  updates.forEach((entry, index) => {
    y = ensureSpace(doc, y, 14);
    const isLast = index === updates.length - 1;

    setDraw(doc, COLORS.violet600);
    doc.setFillColor(...COLORS.violet600);
    doc.circle(MARGIN + 2, y - 1, 1.2, 'F');

    if (!isLast) {
      setDraw(doc, COLORS.slate200);
      doc.line(MARGIN + 2, y + 1, MARGIN + 2, y + 12);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    setColor(doc, COLORS.slate700);
    doc.text(`${formatPdfTimestamp(entry.at)} · ${entry.by || 'System'}`, MARGIN + 7, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    setColor(doc, COLORS.slate900);
    const msgLines = doc.splitTextToSize(String(entry.message || ''), CONTENT_WIDTH - 10);
    doc.text(msgLines, MARGIN + 7, y + 4.5);
    y += 4.5 + msgLines.length * 4.5 + 5;
  });

  return y + 2;
};

const drawMaterialsSection = (doc, y, workOrder) => {
  const plan = workOrder?.materialPlan || [];
  const reconciled = workOrder?.reconciledMaterials || [];
  if (!plan.length && !reconciled.length) return y;

  y = drawSectionHeading(doc, y, 'Tools & materials');

  const lines = [];
  if (plan.length) {
    lines.push('Planned:');
    plan.forEach((item) => {
      lines.push(`• ${item.label} × ${item.plannedQty || 1}`);
    });
  }
  if (reconciled.length) {
    if (lines.length) lines.push('');
    lines.push('Reconciled (used):');
    reconciled.forEach((item) => {
      lines.push(`• ${item.label}: ${item.usedQty ?? '—'} / ${item.plannedQty ?? '—'}`);
    });
  }
  if (workOrder?.sitePhotos?.length) {
    lines.push('');
    lines.push(`${workOrder.sitePhotos.length} site photo(s) attached`);
  }

  return drawTextBox(doc, y, lines.join('\n'), Math.max(18, lines.length * 4.8 + 8));
};

/**
 * Download a ticket / work-order summary PDF with the Sagemcom logo.
 */
export async function downloadCskTicketPdf({
  ticket,
  workOrder,
  reportTitle = 'Ticket report',
  priorityFieldLabel = 'Priority / Timeline',
}) {
  if (!ticket) throw new Error('No ticket data');

  const logoData = await loadLogoDataUrl();
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const generatedAt = new Date().toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const referenceId = ticket.workOrderId || ticket.id;
  let y = drawHeader(doc, logoData, referenceId);
  y = drawReportTitle(doc, y, reportTitle, generatedAt);

  const summaryItems = [
    { label: 'Ticket ID', value: ticket.id },
    { label: 'Type', value: ticket.ticketTypeLabel || ticket.ticketType },
    { label: 'Status', value: ticket.status },
    { label: priorityFieldLabel, value: ticket.timelineLabel || ticket.priorityLabel || ticket.priority },
    { label: 'Opened', value: formatPdfDate(ticket.created) },
    { label: 'Engineer', value: ticket.engineer || 'Unassigned' },
  ];
  if (ticket.workOrderId && ticket.workOrderId !== ticket.id) {
    summaryItems.splice(1, 0, { label: 'Work order', value: ticket.workOrderId });
  }
  y = drawSummaryStrip(doc, y, summaryItems.slice(0, 6));

  y = drawSectionHeading(doc, y, 'Problem description');
  y = drawTextBox(doc, y, ticket.issue, 22);

  const clientFields = [
    { label: 'Client', value: ticket.client },
    { label: 'Contact', value: ticket.contact },
    { label: 'Site address', value: ticket.location },
    { label: 'Coordinates', value: ticket.coordinates },
  ].filter((f) => f.value);

  const assignmentFields = [
    { label: 'Assigned engineer', value: ticket.engineer },
    workOrder ? { label: 'Work order', value: workOrder.id } : null,
    workOrder ? { label: 'Task', value: workOrder.title } : null,
    workOrder ? { label: 'WO status', value: workOrder.status } : null,
    workOrder?.scheduled ? { label: 'Scheduled', value: workOrder.scheduled } : null,
    workOrder?.zone ? { label: 'Zone', value: workOrder.zone } : null,
  ].filter(Boolean);

  if (clientFields.length || assignmentFields.length) {
    y = drawTwoColumnSection(
      doc,
      y,
      'Client & site',
      clientFields.length ? clientFields : [{ label: 'Client', value: '—' }],
      workOrder || ticket.engineer ? 'Assignment' : 'Service details',
      assignmentFields.length ? assignmentFields : [{ label: 'Engineer', value: ticket.engineer || 'Unassigned' }]
    );
  }

  if (workOrder) {
    y = drawMaterialsSection(doc, y, workOrder);
  }

  y = drawTimeline(doc, y, ticket.updates || []);

  if (ticket.clientConfirmed) {
    y = drawSectionHeading(doc, y, 'Client feedback');
    const feedbackLines = [
      'Resolution confirmed: Yes',
      ticket.serviceRating ? `Service rating: ${ticket.serviceRating} / 5` : null,
      ticket.feedbackComment ? `Comment: ${ticket.feedbackComment}` : null,
    ]
      .filter(Boolean)
      .join('\n');
    drawTextBox(doc, y, feedbackLines, 16);
  }

  drawPageFooter(doc);

  const safeId = String(ticket.id || 'ticket').replace(/[^\w-]+/g, '_');
  const dateLabel = new Date().toISOString().slice(0, 10);
  doc.save(`CSK_${safeId}_${dateLabel}.pdf`);
}
